import type { ComponentType } from 'react';
import { useUiStore } from '@/shared/hooks/use-shallow-store';
import { DIALOG_REGISTRY } from './registry';
import type { DialogKind } from './types';

type AnyComp = ComponentType<{ open: true; onClose: () => void } & Record<string, unknown>>;

/**
 * Renders every dialog currently on the stack. Mount once at the app
 * root (inside `<AppShell>`) — feature components only push/pop entries
 * via `useDialog()` and never render dialog components directly.
 *
 * Each entry resolves to the registry component receiving its registered
 * props plus `{ open: true, onClose }`. Close removes the entry from
 * the stack, which unmounts the component (the dialog's own exit
 * animation runs because `AdaptiveDialog` wraps in `AnimatePresence`).
 *
 * Stacking just works: AdaptiveDialog uses `z-50` and React renders
 * later children over earlier ones, so the topmost dialog naturally
 * sits over its parents. Backdrop dismiss only closes the topmost
 * dialog (the click hits the topmost backdrop).
 */
export const DialogHost = () => {
  const dialogs = useUiStore((s) => s.dialogs);
  const closeDialog = useUiStore((s) => s.closeDialog);

  return (
    <>
      {dialogs.map((entry) => {
        const Comp = DIALOG_REGISTRY[entry.kind as DialogKind] as unknown as AnyComp | undefined;
        if (!Comp) {
          if (import.meta.env.DEV) console.error('[DialogHost] no component for kind', entry.kind);
          return null;
        }
        return (
          <Comp
            key={entry.id}
            open
            onClose={() => closeDialog(entry.id)}
            {...(entry.props as unknown as Record<string, unknown>)}
          />
        );
      })}
    </>
  );
};
